import { IcBox, IcRoadAssistance, IcUsers } from '@/assets/images';
import { AgencyType, AssignedUnitShiftStatus, IHeadquarters } from '@/models';

import { ActionButtonItem } from '../../Buttons';

export const UnitResourceFormFields: Record<string, any> = {
  VEHICLE: {
    name: 'vehicles',
    type: 'autocomplete',
    label: 'Vehicles',
    errorLabel: 'vehicle',
    multiple: true,
    limitTags: 3,
    rules: {
      required: 'This field is required.',
    },
  },
  PEOPLE: {
    name: 'users',
    type: 'autocomplete',
    label: 'Employees',
    errorLabel: 'employee',
    multiple: true,
    limitTags: 3,
    rules: {
      required: 'This field is required.',
    },
  },
  EQUIPMENT: {
    name: 'equipments',
    type: 'autocomplete',
    label: 'Equipment',
    errorLabel: 'equipment resource',
    multiple: true,
    limitTags: 3,
    rules: {
      required: 'This field is required.',
    },
  },
  POLYGON_LAYER: {
    name: 'polygons',
    type: 'autocomplete',
    label: 'Polygons',
    errorLabel: 'polygon resource',
    multiple: true,
    limitTags: 3,
    rules: {
      required: 'This field is required.',
    },
  },
};

export const initFilterValues = {
  agencies: [],
  beat: '',
  agencyType: null,
  unitTypes: [],
  name: '',
};

export const agencyTypeItems = [
  {
    label: 'EMS',
    value: AgencyType.EMS,
  },
  {
    label: 'FIRE',
    value: AgencyType.FIRE,
  },
  {
    label: 'POLICE',
    value: AgencyType.POLICE,
  },
  {
    label: 'EMERGENCY MANAGEMENT',
    value: AgencyType.EMERGENCY_MANAGEMENT,
  },
  {
    label: 'MUNICIPAL SERVICES',
    value: AgencyType.MUNICIPAL_SERVICES,
  },
];

export const availableUnitActions = [
  {
    label: 'Direct',
    value: 'assign',
  },
  {
    label: 'Set off-duty',
    value: 'set-off-duty',
  },
];

export const availableHQUnitActions = [
  {
    label: 'Direct',
    value: 'assign',
  },
  {
    label: 'Set off-duty',
    value: 'set-off-duty',
  },
];

export const HEADQUARTER_ACTION_PREFIX = 'assignToHQ:';

export const getHeadquarterActionValue = (headquarterName: string) =>
  `${HEADQUARTER_ACTION_PREFIX}${headquarterName}`;

export const getHeadquarterNameFromAction = (
  action: string | number,
): string | null => {
  if (
    typeof action !== 'string' ||
    !action.startsWith(HEADQUARTER_ACTION_PREFIX)
  ) {
    return null;
  }

  return action.slice(HEADQUARTER_ACTION_PREFIX.length);
};

export const getHeadquarterAssignActionItems = (
  headquarters: IHeadquarters[],
) => {
  return headquarters.map((headquarter) => ({
    label: `Assign to ${headquarter.name}`,
    value: getHeadquarterActionValue(headquarter.name),
  }));
};

export const getAvailableUnitActions = (
  headquarters: IHeadquarters[],
  isDispatchToHQEnabled?: boolean,
) => {
  return isDispatchToHQEnabled && headquarters.length
    ? [
        availableHQUnitActions[0],
        ...getHeadquarterAssignActionItems(headquarters),
        availableHQUnitActions[1],
      ]
    : availableUnitActions;
};

export const unitShiftStatusItems: ActionButtonItem[] = [
  {
    label: 'Assigned',
    value: AssignedUnitShiftStatus.ASSIGNED,
  },
  {
    label: 'En Route',
    value: AssignedUnitShiftStatus.EN_ROUTE,
  },
  {
    label: 'In The Area',
    value: AssignedUnitShiftStatus.IN_THE_AREA,
  },
  {
    label: 'On scene',
    value: AssignedUnitShiftStatus.ON_SCENE,
  },
  {
    label: 'In progress',
    value: AssignedUnitShiftStatus.PROGRESS,
  },
];

export const unitShiftActionItems = [
  {
    label: 'Complete',
    value: AssignedUnitShiftStatus.COMPLETED,
  },
  {
    label: 'Cancel',
    value: AssignedUnitShiftStatus.CANCELED,
  },
];

export const unitShiftHQActionItems = [
  {
    label: 'Assign to call',
    value: 'assignToCall',
  },
  {
    label: 'Complete',
    value: AssignedUnitShiftStatus.COMPLETED,
  },
  {
    label: 'Cancel',
    value: AssignedUnitShiftStatus.CANCELED,
  },
];

export const getUnitShiftHQActionItems = (
  headquarters: IHeadquarters[],
  currentHeadquarterName?: string | null,
) => [
  ...getHeadquarterAssignActionItems(
    headquarters.filter(
      (headquarter) => headquarter.name !== currentHeadquarterName,
    ),
  ),
  ...unitShiftHQActionItems,
];

export const unitShiftCallActionItems = [
  {
    label: 'Complete',
    value: AssignedUnitShiftStatus.COMPLETED,
  },
  {
    label: 'Cancel',
    value: AssignedUnitShiftStatus.CANCELED,
  },
];

export const unitShiftReassignActionItems = [
  {
    label: 'Complete and reassign',
    value: AssignedUnitShiftStatus.COMPLETED,
  },
  {
    label: 'Cancel and reassign',
    value: AssignedUnitShiftStatus.CANCELED,
  },
  {
    label: 'Queue',
    value: AssignedUnitShiftStatus.QUEUED,
  },
];

export const unitShiftStandbyActionItems = [
  {
    label: 'Direct',
    value: 'assign',
  },
  {
    label: 'Set on duty',
    value: 'setOnDuty',
  },
];

export const unitShiftStandbyHQActionItems = [
  {
    label: 'Direct',
    value: 'assign',
  },
  {
    label: 'Set on duty',
    value: 'setOnDuty',
  },
];

export const getUnitShiftCallActionItems = (headquarters: IHeadquarters[]) => [
  ...getHeadquarterAssignActionItems(headquarters),
  ...unitShiftCallActionItems,
];

export const getUnitShiftStandbyActionItems = (
  headquarters: IHeadquarters[],
  isDispatchToHQEnabled?: boolean,
) => {
  return isDispatchToHQEnabled && headquarters.length
    ? [
        unitShiftStandbyHQActionItems[0],
        ...getHeadquarterAssignActionItems(headquarters),
        unitShiftStandbyHQActionItems[1],
      ]
    : unitShiftStandbyActionItems;
};

export const unitShiftViewItems = [
  {
    name: 'users',
    icon: IcUsers,
    items: [],
  },
  {
    name: 'vehicles',
    icon: IcRoadAssistance,
    items: [],
  },
  {
    name: 'equipments',
    icon: IcBox,
    items: [],
  },
];

export const unitFilterMenuTabs = [
  {
    value: 'currentFilter',
    label: 'Filter',
  },
  {
    value: 'savedFilters',
    label: 'Saved filters',
  },
];
